import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Search, Facebook, Linkedin, Music2, LayoutTemplate, FlaskConical, Activity,
  Target, MousePointerClick, DollarSign, BarChart3, Sparkles, Layers,
  Gauge, ShieldCheck, Code2, Eye, Users, TrendingUp,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "google-ads": {
    eyebrow: "Paid Ads · Channels",
    title: "Google Ads",
    sub: "Search, Performance Max, YouTube and Shopping tuned for ROAS.",
    metaDesc: "Google Ads management: Search, PMax, YouTube and Shopping campaigns built around CAC and ROAS targets.",
    intro:
      "We build Google Ads accounts that print profit — tight Search structures, PMax with proper feed governance, YouTube for top-of-funnel and Shopping with a healthy merchant feed. Every campaign tied to CAC and ROAS targets, not vanity clicks.",
    features: [
      { icon: Search, title: "Search campaigns", description: "SKAG-lite structures, exact + phrase, negatives discipline." },
      { icon: Sparkles, title: "Performance Max", description: "Feed-first PMax with asset groups by audience and intent." },
      { icon: Eye, title: "YouTube ads", description: "Hooks, demand-gen and remarketing video funnels." },
      { icon: Layers, title: "Shopping & feeds", description: "Merchant Center governance and feed optimization." },
      { icon: Target, title: "Smart bidding", description: "tCPA / tROAS with proper conversion values and signals." },
      { icon: BarChart3, title: "GA4 + Conversions API", description: "Server-side tracking for accurate ROAS data." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Account, conversions, and competitor benchmarks." },
      { step: "02", title: "Build", description: "Restructure, feeds, creative and tracking." },
      { step: "03", title: "Launch", description: "Phased rollout with daily monitoring." },
      { step: "04", title: "Scale", description: "Weekly experiments and budget reallocation." },
    ],
    deliverables: ["Account audit + forecast", "Restructured campaigns", "Asset + feed builds", "Server-side tracking", "Weekly reports", "Monthly strategy review"],
    techStack: ["Google Ads", "Merchant Center", "GA4", "GTM Server", "Looker Studio"],
    pricing: [
      { name: "Starter", price: "$2k", period: "month", features: ["Up to $20k spend", "Search + Shopping", "Weekly reports"] },
      { name: "Growth", price: "$5k", period: "month", highlighted: true, features: ["Up to $100k spend", "All campaign types", "Creative production"] },
      { name: "Performance", price: "10% of spend", features: ["$100k+ spend", "Dedicated team", "Custom dashboards"] },
    ],
    faqs: [
      { q: "Do you manage PMax?", a: "Yes — with strict feed and asset-group governance to avoid black-box waste." },
      { q: "Will you build landing pages?", a: "Yes — every campaign gets dedicated, tested landers." },
    ],
  },
  "meta-ads": {
    eyebrow: "Paid Ads · Channels",
    title: "Meta Ads",
    sub: "Facebook and Instagram ads with creative volume and proper tracking.",
    metaDesc: "Meta Ads management for Facebook and Instagram: creative production, audience strategy and Conversions API tracking.",
    intro:
      "Meta is a creative game. We ship 15–30 new creatives per month, run structured testing, and feed the algorithm clean signal via Conversions API — so CPMs work for you instead of against.",
    features: [
      { icon: Sparkles, title: "Creative production", description: "Static, motion and UGC in weekly batches." },
      { icon: Users, title: "Audience strategy", description: "Broad, interest, LAL and retargeting layers done right." },
      { icon: Target, title: "Campaign structure", description: "ASC, CBO and manual structures matched to scale." },
      { icon: Activity, title: "Conversions API", description: "Server-side events for accurate ROAS in iOS 14.5+ world." },
      { icon: Eye, title: "Creative testing", description: "Concept → format → variation testing pyramids." },
      { icon: BarChart3, title: "Triple Whale / Northbeam", description: "Cross-channel attribution for true incremental ROAS." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Account, creative, tracking and audience review." },
      { step: "02", title: "Build", description: "Restructure, CAPI, creative pipeline." },
      { step: "03", title: "Launch", description: "Phased rollout with daily creative iteration." },
      { step: "04", title: "Scale", description: "Winners → ASC scaling with budget pacing." },
    ],
    deliverables: ["Account audit", "Restructured campaigns", "15+ creatives/month", "Conversions API setup", "Weekly creative reports", "Monthly strategy review"],
    techStack: ["Meta Ads", "Conversions API", "Triple Whale", "Northbeam", "Motion", "Figma"],
    pricing: [
      { name: "Starter", price: "$2.5k", period: "month", features: ["Up to $25k spend", "10 creatives / month", "Weekly reports"] },
      { name: "Growth", price: "$5.5k", period: "month", highlighted: true, features: ["Up to $100k spend", "20 creatives / month", "UGC included"] },
      { name: "Performance", price: "10% of spend", features: ["$100k+ spend", "30+ creatives / month", "Dedicated team"] },
    ],
    faqs: [
      { q: "Do you produce UGC?", a: "Yes — we have a creator roster for sourced and scripted UGC across niches." },
      { q: "Does iOS 14.5 still matter?", a: "Yes — that's why CAPI + modeled conversions + Triple Whale are non-negotiable." },
    ],
  },
  "linkedin-ads": {
    eyebrow: "Paid Ads · Channels",
    title: "LinkedIn Ads",
    sub: "B2B demand generation and ABM that actually fills pipeline.",
    metaDesc: "LinkedIn Ads management: ABM, demand-gen, Conversation and Document ads with CRM-integrated tracking.",
    intro:
      "LinkedIn is the highest-intent B2B channel and the most expensive — so creative, targeting and offer have to be sharp. We run ABM playbooks, demand-gen content funnels and ICP targeting with CRM closed-loop reporting.",
    features: [
      { icon: Target, title: "ABM targeting", description: "Account lists by firmographics, intent and tech stack." },
      { icon: Users, title: "Audience layering", description: "Job titles + functions + seniority refined per offer." },
      { icon: Sparkles, title: "Creative formats", description: "Single image, video, document, conversation and Thought Leader ads." },
      { icon: Layers, title: "Demand-gen funnels", description: "Content → retargeting → MQL → sales offers." },
      { icon: Activity, title: "Conversions API", description: "Server-side LinkedIn Insight + CRM lift modeling." },
      { icon: TrendingUp, title: "Pipeline attribution", description: "MQL, SQL and revenue tied back to LinkedIn campaigns." },
    ],
    process: [
      { step: "01", title: "ICP & offer", description: "Targeting, offer, and creative concepts." },
      { step: "02", title: "Build", description: "Campaigns, audiences, creative, CAPI." },
      { step: "03", title: "Launch", description: "Phased rollout with frequency caps." },
      { step: "04", title: "Optimize", description: "Bid + audience tuning and creative refresh." },
    ],
    deliverables: ["ABM target lists", "Campaign builds", "Creative production", "Insight Tag + CAPI", "CRM closed-loop reports", "Monthly pipeline review"],
    techStack: ["LinkedIn Ads", "Insight Tag", "HubSpot", "Salesforce", "6sense", "Bombora"],
    pricing: [
      { name: "Pilot", price: "$3k", period: "month", features: ["Up to $20k spend", "1 offer", "Monthly review"] },
      { name: "Growth", price: "$7k", period: "month", highlighted: true, features: ["Up to $75k spend", "Multi-offer funnel", "CRM integration"] },
      { name: "Enterprise", price: "$15k", period: "month", features: ["$100k+ spend", "Full ABM program", "Pipeline reporting"] },
    ],
    faqs: [
      { q: "Min spend on LinkedIn?", a: "$15k/month is the floor for meaningful tests; below that, frequency and learning are too thin." },
      { q: "ABM tools needed?", a: "Optional — we work with 6sense, Demandbase, Clearbit or your own list." },
    ],
  },
  "tiktok-ads": {
    eyebrow: "Paid Ads · Channels",
    title: "TikTok Ads",
    sub: "Native-feeling creative and Spark Ads that scale without burning brand.",
    metaDesc: "TikTok Ads: creator-led UGC, Spark Ads, Smart Performance Campaigns and Events API setup for ROAS.",
    intro:
      "TikTok rewards native creative and punishes ads that look like ads. We pair creator-led UGC with proper Spark Ads, SPC bidding and Events API so you scale spend without tanking ROAS or hand-feeding the algorithm bad data.",
    features: [
      { icon: Sparkles, title: "Creator UGC", description: "Brief, source and ship 10–20 creator videos per month." },
      { icon: Music2, title: "Spark Ads", description: "Boost real creator posts for higher engagement and lower CPM." },
      { icon: Target, title: "SPC bidding", description: "Smart Performance Campaigns with proper conversion signals." },
      { icon: Activity, title: "Events API", description: "Server-side TikTok pixel + conversions for iOS-accurate data." },
      { icon: Eye, title: "Concept testing", description: "Hook-first testing — 3 hooks × 3 bodies × 3 CTAs." },
      { icon: BarChart3, title: "Incrementality", description: "Geo holdouts and lift studies vs claimed ROAS." },
    ],
    process: [
      { step: "01", title: "Concept", description: "Hooks, formats and creator briefs." },
      { step: "02", title: "Produce", description: "Source UGC and Spark Ads." },
      { step: "03", title: "Launch", description: "SPC + manual structures in parallel." },
      { step: "04", title: "Scale", description: "Winners → broader audiences and budget." },
    ],
    deliverables: ["Creator + concept brief", "10+ UGC videos / month", "Spark Ads setup", "Events API", "Weekly creative report", "Monthly incrementality view"],
    techStack: ["TikTok Ads", "TikTok Events API", "Spark Ads", "Triple Whale", "Motion", "CapCut"],
    pricing: [
      { name: "Starter", price: "$3k", period: "month", features: ["Up to $25k spend", "6 UGC videos / month"] },
      { name: "Growth", price: "$6k", period: "month", highlighted: true, features: ["Up to $100k spend", "12 UGC videos / month", "Spark Ads"] },
      { name: "Performance", price: "10% of spend", features: ["$100k+ spend", "20+ videos / month", "Dedicated team"] },
    ],
    faqs: [
      { q: "Do you source creators?", a: "Yes — vetted roster across DTC, beauty, fitness, finance and B2B niches." },
      { q: "Will you repurpose ads from Meta?", a: "Only as a starting point — TikTok-native creative consistently wins." },
    ],
  },
  "landing-pages": {
    eyebrow: "Paid Ads · Optimization",
    title: "Landing Pages",
    sub: "Dedicated landers built to convert paid traffic.",
    metaDesc: "Paid-traffic landing page design and development: fast, conversion-optimized pages with built-in A/B testing.",
    intro:
      "Sending ad traffic to your homepage is the easiest way to waste budget. We design and build dedicated landing pages per campaign — fast, mobile-first, conversion-focused, and instrumented for A/B testing from day one.",
    features: [
      { icon: LayoutTemplate, title: "Conversion design", description: "Above-the-fold offer, proof, and one clear CTA." },
      { icon: Gauge, title: "Core Web Vitals", description: "LCP under 1.5s — fast loads protect your Quality Score." },
      { icon: MousePointerClick, title: "Mobile-first", description: "70%+ of paid traffic is mobile — designed for thumbs." },
      { icon: FlaskConical, title: "Built for testing", description: "Variant slots wired into your testing tool from day one." },
      { icon: Code2, title: "Fast stack", description: "Next.js, Astro or our framework — your call." },
      { icon: ShieldCheck, title: "Privacy + tracking", description: "GTM server, CAPI events and consent compliance." },
    ],
    process: [
      { step: "01", title: "Offer + brief", description: "Audience, offer and message-match plan." },
      { step: "02", title: "Design", description: "Wireframe → high-fidelity in 1 week." },
      { step: "03", title: "Build", description: "Production-ready with tracking baked in." },
      { step: "04", title: "Launch & test", description: "Connect to ads and run first A/B." },
    ],
    deliverables: ["Offer + messaging brief", "Wireframe + design", "Production build", "Tracking + CAPI", "First A/B test", "Performance report"],
    techStack: ["Next.js", "Astro", "Webflow", "Unbounce", "GTM Server", "VWO", "Figma"],
    pricing: [
      { name: "Single page", price: "$3.5k", features: ["1 lander", "Design + build", "Tracking setup"] },
      { name: "Campaign pack", price: "$9k", period: "project", highlighted: true, features: ["3 landers", "Shared component library", "A/B framework"] },
      { name: "Retainer", price: "$5k", period: "month", features: ["2–4 landers / month", "Continuous CRO", "Test reports"] },
    ],
    faqs: [
      { q: "Will you use our CMS?", a: "Yes when it's fast enough; otherwise we host on a dedicated, performant stack." },
      { q: "Do you copywrite?", a: "Yes — direct-response copy is part of every project." },
    ],
  },
  "ab-testing": {
    eyebrow: "Paid Ads · Optimization",
    title: "A/B Testing",
    sub: "A statistically-rigorous CRO program that lifts revenue per visitor.",
    metaDesc: "A/B testing and CRO program: hypothesis backlog, experiment design, statistical analysis and continuous wins.",
    intro:
      "Most testing programs ship 'winners' that don't replicate. We run a research-driven CRO program — qualitative + quantitative inputs, properly-powered experiments, sequential testing safeguards — so wins actually compound into revenue.",
    features: [
      { icon: FlaskConical, title: "Hypothesis backlog", description: "PXL/ICE-scored backlog from research, heuristics and data." },
      { icon: Activity, title: "Experiment design", description: "Proper sample sizes, MDE and stopping rules." },
      { icon: BarChart3, title: "Stats engine", description: "Bayesian or frequentist; sequential testing safe-guards." },
      { icon: Eye, title: "Session research", description: "Hotjar, FullStory and user interviews fuel hypotheses." },
      { icon: TrendingUp, title: "Test velocity", description: "4–8 tests / month on traffic that supports it." },
      { icon: ShieldCheck, title: "Quality controls", description: "QA, SRM checks and post-test holdouts." },
    ],
    process: [
      { step: "01", title: "Research", description: "Analytics, sessions, surveys and heuristics review." },
      { step: "02", title: "Prioritize", description: "Hypothesis backlog scored by impact and confidence." },
      { step: "03", title: "Run", description: "Design, build, QA and ship experiments." },
      { step: "04", title: "Learn", description: "Analyze, document and iterate." },
    ],
    deliverables: ["Research report", "Hypothesis backlog", "Monthly experiments", "Stats analysis", "Test learnings library", "Quarterly program review"],
    techStack: ["VWO", "Optimizely", "Convert", "Hotjar", "FullStory", "GrowthBook"],
    pricing: [
      { name: "Starter", price: "$3.5k", period: "month", features: ["2 tests / month", "Tool of your choice", "Monthly reporting"] },
      { name: "Growth", price: "$7k", period: "month", highlighted: true, features: ["4–6 tests / month", "Full research", "Bi-weekly reviews"] },
      { name: "Enterprise", price: "$15k", period: "month", features: ["8+ tests / month", "Dedicated CRO team", "Custom stats engine"] },
    ],
    faqs: [
      { q: "How much traffic do I need?", a: "Roughly 30k+ monthly conversions per surface to run 4-week tests at reasonable MDEs." },
      { q: "What if I have low traffic?", a: "We shift to research + best-practice changes + sequential micro-tests instead of forcing underpowered A/Bs." },
    ],
  },
  "tracking-pixels": {
    eyebrow: "Paid Ads · Optimization",
    title: "Tracking & Pixels",
    sub: "Server-side tracking that survives iOS, ad blockers and consent.",
    metaDesc: "Server-side tracking: GTM server containers, Conversions API, GA4, consent mode and end-to-end QA.",
    intro:
      "Browser pixels are broken. We deploy server-side GTM, Conversions APIs for Meta, Google, TikTok and LinkedIn, GA4 with proper event design, and consent mode — so your ad platforms see clean, complete data and bid you up instead of throttling you.",
    features: [
      { icon: Code2, title: "GTM server container", description: "First-party endpoint on your domain, fully owned." },
      { icon: Activity, title: "Conversions APIs", description: "Meta CAPI, Google Enhanced, TikTok EAPI, LinkedIn CAPI." },
      { icon: BarChart3, title: "GA4 event design", description: "Clean event schema, custom dimensions and conversions." },
      { icon: ShieldCheck, title: "Consent mode v2", description: "Cookie banner integration with modeled conversions." },
      { icon: Eye, title: "End-to-end QA", description: "Event QA with Chrome DevTools, Tag Assistant and Meta Test Events." },
      { icon: Layers, title: "Cross-domain + identity", description: "User_id stitching, FB cookie restoration, CRM sync." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Current tracking, gaps and identity stitching." },
      { step: "02", title: "Design", description: "Event schema, server container, consent." },
      { step: "03", title: "Build", description: "Implement and QA across all channels." },
      { step: "04", title: "Monitor", description: "Ongoing event QA and platform parity checks." },
    ],
    deliverables: ["Tracking audit", "Event schema doc", "GTM server container", "Conversions API per channel", "Consent mode integration", "QA + monitoring dashboard"],
    techStack: ["GTM Server", "Stape", "Meta CAPI", "Google Enhanced", "TikTok Events API", "LinkedIn CAPI", "GA4", "Cookiebot"],
    pricing: [
      { name: "Single channel", price: "$3k", features: ["1 CAPI", "GA4 events", "QA"] },
      { name: "Full stack", price: "$9k", period: "project", highlighted: true, features: ["GTM server", "All CAPIs", "Consent mode", "Docs"] },
      { name: "Managed", price: "$2.5k", period: "month", features: ["Hosting", "Monitoring", "Continuous QA"] },
    ],
    faqs: [
      { q: "Do I need a server container?", a: "If you spend on ads and care about ROAS, yes — it's table stakes now." },
      { q: "Can you host the server endpoint?", a: "Yes — on Stape or your own GCP project; you own the data either way." },
    ],
  },
};

export const Route = createFileRoute("/marketing/paid-advertising/$child")({
  component: PaidAdsChild,
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item };
  },
  head: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) return {};
    return {
      meta: [
        { title: `${item.title} — CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: `/marketing/paid-advertising/${params.child}` },
      ],
      links: [{ rel: "canonical", href: `https://coderlity.com/marketing/paid-advertising/${params.child}` }],
    };
  },
});

function PaidAdsChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
